import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export class PccProgressNoteParams {
	startDate?: string;

	endDate?: string;
}

export class PccProgressNoteSection {
	/**
	 * Each section's name as titled in the progress note.
	 */
	name = '';

	/**
	 * The details recorded about the patient in the section of the progress note.
	 */
	value = '';

	constructor(partial: Partial<PccProgressNoteSection>) {
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails(prefix = '') {
		const details = [];
		if (this.name) details.push(`Name: ${this.name}`);
		if (this.value) details.push(`Value: ${this.value}`);
		return `${prefix}${details.join(', ')}`;
	}
}

export class PccProgressNoteCode {
	/**
	 * Represents the coding system used to share the concept.
	 */
	system: string;

	/**
	 * Represents the version of the coding system.
	 */
	version: string;

	/**
	 * Represents the code as per the coding system.
	 */
	code: string;

	/**
	 * Represents the code display name as per the coding system.
	 */
	display: string;

	/**
	 * PointClickCare error code
	 */
	errorCode?: string;

	/**
	 * Error description
	 */
	errorMessage?: string;
}

export class PccProgressNote extends PccAuditInfo {

	/**
	 * Unique identification number associated with the progress note.
	 */
	progressNoteId: number;

	/**
	 * Represents the progress note type. Progress note type is used to categorize the notes.
	 */
	progressNoteType: string;

	/**
	 * National Provider ID for the PointClickCare user who completed the progress note for the patient.
	 */
	createdByNpi: string;

	/**
	 * The effective date and time the progress note was created or started. The date and time are expressed in UTC in the following format YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	effectiveDate: string;

	/**
	 * PointClickCare's unique identifier for a facility within the organization.
	 */
	facId: number;

	/**
	 * Optional field that represents the type of care plan item associated with the progress note. For example, any specific intervention, goal, or task.
	 */
	focus?: string;

	/**
	 * Represents if the progress note is a follow-up to a previous progress note.
	 */
	followUpTo?: Pick<PccProgressNote, 'createdBy' | 'effectiveDate' | 'progressNoteId' | 'progressNoteType'>;

	/**
	 * PointClickCare's unique identifier for the organization.
	 */
	orgUuid: string;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.
	 *
	 * When a patient is admitted to a facility within an organization, they are assigned a patientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new patientId. The patientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same patientId.
	 *
	 * This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.
	 */
	patientId: number;

	/**
	 * List of all sections within the progress note.
	 */
	sections: PccProgressNoteSection[];

	noteCode?: {
		/**
		 * List of representation of a concept in a given coding system.
		 */
		codings: PccProgressNoteCode[];
	};

	details: string;

	constructor(partial: Partial<PccProgressNote>) {
		super();
		Object.assign(this, partial);
		if (this.sections) this.sections = this.sections.map((section) => new PccProgressNoteSection(section));
	}

	@Expose({ name: 'details' })
	getDetails() {
		const details = [`Data Type: progressNote`];
		if (this.progressNoteId) details.push(`Progress Note ID: ${this.progressNoteId}`);
		if (this.progressNoteType) details.push(`Progress Note Type: ${this.progressNoteType}`);
		if (this.createdByNpi) details.push(`Created By NPI: ${this.createdByNpi}`);
		if (this.effectiveDate) details.push(`Effective Date: ${this.effectiveDate}`);
		if (this.focus) details.push(`Focus: ${this.focus}`);
		if (this.followUpTo) details.push(`Follow Up To note created by ${this.followUpTo.createdBy} on ${this.followUpTo.effectiveDate} of type ${this.followUpTo.progressNoteType}`);
		if (this.sections) details.push(`${this.sections.map((section) => `${section.getDetails('\t')}`).join('\n')}`);
		return `${details.join(', ')}`;
	}

}
